/**
 * DetailView2 Component
 *
 * Displays 0-9 selected photos in a responsive grid layout that scales to fit
 * available space without scrolling.
 *
 * Features:
 * - Scales to fit container (no overflow/scrolling)
 * - Responsive grid (1-3 columns, 1-3 rows based on photo count)
 * - Maximum 9 photos displayed (silent truncation)
 * - Empty state for zero photos
 * - Renders DetailViewItem2 for each photo
 * - Synchronized zoom/pan across all photos via ZoomPanContext
 *
 * @module DetailView2
 */

import React, { useState, useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { DetailViewItem2 } from '../DetailViewItem2';
import { PhotoContextMenu } from '../PhotoContextMenu';
import { ZoomPanContext } from '@/contexts/ZoomPanContext';
import { ViewMode } from '@/contexts/ViewModeContext';
import { useAuth } from '@/services/auth';
import type { ZoomLevel } from '@/hooks/useZoomPan';
import type { DetailView2Props } from './DetailView2.types';

/**
 * DetailView2 - Container component for detail view grid
 *
 * Grid Layout Rules (scales to fit container without scrolling):
 * - 0 photos: Empty state message
 * - 1 photo: 1 column × 1 row (full space)
 * - 2 photos: 2 columns × 1 row (side-by-side)
 * - 3 photos: 3 columns × 1 row (horizontal strip)
 * - 4-6 photos: 3 columns × 2 rows
 * - 7-9 photos: 3 columns × 3 rows (max grid)
 * - 10+ photos: Only first 9 displayed (silent truncation)
 *
 * Images use object-contain to preserve aspect ratios within their grid cells.
 *
 * Synchronized Zoom/Pan:
 * - Provides ZoomPanContext to all DetailViewItem2 children
 * - Coordinates zoom level and pan position across all images
 * - Uses percentage-based pan to handle different aspect ratios
 * - Waits for all images to load before applying zoom/pan
 *
 * @example
 * ```tsx
 * <DetailView2
 *   photos={selectedPhotos}
 *   className="bg-surface-1"
 * />
 * ```
 */
export const DetailView2: React.FC<DetailView2Props> = ({
  photos,
  className,
  initialZoomLevel = 'fit',
  onZoomChange,
}) => {
  // Feature 081: photo-versioning - Get current user for ownership check
  const { user } = useAuth();
  const currentUserId = user?.id;

  // Limit to maximum 9 photos
  const displayedPhotos = photos.slice(0, 9);
  const photoCount = displayedPhotos.length;

  // Shared zoom/pan state for synchronization (T039-T040)
  // Feature 646: Initialize from prop to enable session persistence
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>(initialZoomLevel);
  const [panXPercent, setPanXPercent] = useState(0);
  const [panYPercent, setPanYPercent] = useState(0);
  const [loadingCount, setLoadingCount] = useState(0);
  const [isPanning] = useState(false); // Managed by individual instances

  // Feature 646: Sync internal zoom state when prop changes (from dropdown/keyboard)
  useEffect(() => {
    setZoomLevel(initialZoomLevel);
  }, [initialZoomLevel]);

  // Feature 646-T025: Reset pan position when switching photos
  // When photos change (user navigates to different photo), reset pan to center
  useEffect(() => {
    setPanXPercent(0);
    setPanYPercent(0);
  }, [photos]);

  // Context callbacks (T041-T043)
  const handleZoomChange = useCallback(
    (level: ZoomLevel, cursorPercentX?: number, cursorPercentY?: number) => {
      setZoomLevel(level);

      // Feature 646: Notify parent of zoom change for session persistence
      onZoomChange?.(level);

      // If cursor position provided, update pan to center on that point
      if (cursorPercentX !== undefined && cursorPercentY !== undefined) {
        setPanXPercent(cursorPercentX);
        setPanYPercent(cursorPercentY);
      } else if (level === 'fit') {
        // Reset pan when returning to fit
        setPanXPercent(0);
        setPanYPercent(0);
      }
    },
    [onZoomChange]
  );

  const handlePanChange = useCallback((xPercent: number, yPercent: number) => {
    setPanXPercent(xPercent);
    setPanYPercent(yPercent);
  }, []);

  const handleLoadingChange = useCallback((isLoading: boolean) => {
    setLoadingCount(prev => {
      const newCount = isLoading ? prev + 1 : Math.max(0, prev - 1);
      return newCount;
    });
  }, []);

  // Empty state
  if (photoCount === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-center h-full',
          'text-muted p-8',
          className
        )}
      >
        <p>Select up to 9 photos to view details</p>
      </div>
    );
  }

  // Determine grid columns and rows based on photo count
  // Grid will scale to fit container without scrolling
  const gridClasses = cn(
    'grid gap-4 h-full',
    // Column layout
    photoCount === 1 && 'grid-cols-1',
    photoCount === 2 && 'grid-cols-2',
    photoCount >= 3 && 'grid-cols-3',
    // Row layout (auto-rows ensures equal height distribution)
    photoCount <= 3 && 'grid-rows-1',
    photoCount >= 4 && photoCount <= 6 && 'grid-rows-2',
    photoCount >= 7 && 'grid-rows-3'
  );

  // Context value (T044)
  const contextValue = {
    zoomLevel,
    panXPercent,
    panYPercent,
    loadingCount,
    isPanning,
    onZoomChange: handleZoomChange,
    onPanChange: handlePanChange,
    onLoadingChange: handleLoadingChange,
  };

  return (
    <ZoomPanContext.Provider value={contextValue}>
      <ViewMode.FullscreenTarget>
        <ViewMode.DimmedFocus>
          <div className={cn('h-full w-full p-4', className)}>
            <div className={gridClasses}>
              {displayedPhotos.map((photo) => {
                // Feature 081: photo-versioning - Check if current user owns this photo
                const isOwner = currentUserId ? photo.photoData.userId === currentUserId : false;

                return (
                  <PhotoContextMenu
                    key={photo.photoData.id}
                    photoId={photo.photoData.id}
                    isOwner={isOwner}
                  >
                    <DetailViewItem2 photo={photo} />
                  </PhotoContextMenu>
                );
              })}
            </div>
          </div>
        </ViewMode.DimmedFocus>
      </ViewMode.FullscreenTarget>
    </ZoomPanContext.Provider>
  );
};

DetailView2.displayName = 'DetailView2';
